import {
  AdditionalForm,
  HistoryAdditionalForm,
} from "../interfaces/additionalForm";
import { AdditionalFormType } from "../interfaces/additionalFormType";
import { Client } from "../interfaces/client";
import { fetchWithAuth } from "./fetchWithAuth";
import { convertFileToBase64 } from "./helpers";

const PROD_APP_URL = process.env.REACT_APP_PROD_APP_URL || "";
const API_URL = process.env.REACT_APP_API_URL || "";
const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || "";
const PROD_API_URL = process.env.REACT_APP_PROD_API_URL || "";

const apiBaseUrl =
  window.location.hostname === "localhost"
    ? API_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_API_URL
      : DEV_API_URL;

export const getAdditionalForm = async (
  clientID: number,
  typeID: number,
): Promise<AdditionalForm | null> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const response = await fetch(
    `${apiBaseUrl}/api/AdditionalForms/GetAdditionalFormByType?ClientID=${clientID}&TypeID=${typeID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch additional form for ClientID ${clientID} and TypeID ${typeID}.`,
    );
  }

  if (response.status === 204) {
    return null;
  }

  const additionalForm: AdditionalForm = await response.json();
  return additionalForm;
};

export const uploadSignature = async (file: File, clientID: number) => {
  if (!apiBaseUrl) throw new Error("API URL is not defined");

  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(
    `${apiBaseUrl}/api/AdditionalForms/UploadSignature?clientID=${clientID}`,
    {
      method: "POST",
      body: formData,
    },
  );

  if (!response.ok) {
    throw new Error("Failed to upload signature.");
  }

  return response.json();
};

export const uploadUserAgreement = async (clientID: number, file?: File) => {
  if (!apiBaseUrl) throw new Error("API URL is not defined");

  const formData = new FormData();

  if (file) {
    formData.append("file", file);
  }

  const response = await fetch(
    `${apiBaseUrl}/api/AdditionalForms/UploadUserAgreement?clientID=${clientID}`,
    {
      method: "POST",
      body: formData,
    },
  );

  if (!response.ok) {
    throw new Error("Failed to upload signature.");
  }

  return response.json();
};

export const uploadPageDecoration = async (file: File, clientID: number) => {
  if (!apiBaseUrl) throw new Error("API URL is not defined");

  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(
    `${apiBaseUrl}/api/AdditionalForms/uploadPageDecoration?clientID=${clientID}`,
    {
      method: "POST",
      body: formData,
    },
  );

  if (!response.ok) {
    throw new Error("Failed to upload logo.");
  }

  return response.json();
};

export const uploadSSNTraceForm = async (file: File, clientID: number) => {
  if (!apiBaseUrl) throw new Error("API URL is not defined");

  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(
    `${apiBaseUrl}/api/AdditionalForms/UploadSSNTraceForm?clientID=${clientID}`,
    {
      method: "POST",
      body: formData,
    },
  );

  if (!response.ok) {
    throw new Error("Failed to upload ssn form.");
  }

  return response.json();
};

export const uploadCreditReportForm = async (file: File, clientID: number) => {
  if (!apiBaseUrl) throw new Error("API URL is not defined");

  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(
    `${apiBaseUrl}/api/AdditionalForms/UploadCreditReportForm?clientID=${clientID}`,
    {
      method: "POST",
      body: formData,
    },
  );

  if (!response.ok) {
    throw new Error("Failed to upload ssn form.");
  }

  return response.json();
};

export const getAdditionalFormsByClientID = async (
  clientID: number,
): Promise<AdditionalForm[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/AdditionalForms/GetAdditionalFormsByClientID/${clientID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch additional forms: ${response.statusText}`);
  }

  const additionalForms: AdditionalForm[] = await response.json();
  return additionalForms;
};

export const getAdditionalFormTypes = async (): Promise<
  AdditionalFormType[]
> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/AdditionalForms/GetAdditionalFormTypes`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch additional form types: ${response.statusText}`,
    );
  }

  const additionalFormTypes: AdditionalFormType[] = await response.json();
  return additionalFormTypes;
};

export const uploadClientAdditionalForm = async (
  client: Client,
  selectedAdditionalFormType: AdditionalFormType,
  file: File,
): Promise<boolean> => {
  try {
    const { base64Data, contentType } = await convertFileToBase64(file);

    const formData = {
      client,
      selectedAdditionalFormType,
      fileBase64: base64Data,
      contentType,
    };

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/AdditionalForms/UploadClientAdditionalForm`,

      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      console.error("Failed to upload client additional form.");
      return false;
    }

    console.log("Form successfully uploaded.");
    return true;
  } catch (error) {
    console.error("Error while uploading form:", error);
    return false;
  }
};

export const downloadBlankUserAgreementForm =
  async (): Promise<Response | null> => {
    try {
      const response = await fetchWithAuth(
        `${apiBaseUrl}/api/AdditionalForms/DownloadBlankUserAgreement`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!response.ok)
        throw new Error("Failed to download user agreement Form");

      return response;
    } catch (error) {
      console.error("Error downloading user agreement:", error);
      return null;
    }
  };

export const deleteAdditionalForm = async (
  clientID: number,
  formTypeID: number,
): Promise<boolean> => {
  try {
    const token = localStorage.getItem("token");
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/AdditionalForms/DeleteAdditionalForm`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ clientID, formTypeID }),
      },
    );

    return response.ok;
  } catch (error) {
    console.error("Delete form error:", error);
    return false;
  }
};

export const getAdditionalFormsHistory = async (
  clientID: number,
): Promise<HistoryAdditionalForm[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/AdditionalForms/GetAdditionalFormsHistory/${clientID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error("Failed to fetch additional forms history");
  }

  const data: HistoryAdditionalForm[] = await response.json();
  return data;
};
